import { useAtom } from 'jotai';
import { RefObject, useCallback, useRef } from 'react';
import { toaster } from '@optiaxiom/react';
import { addEdge, Connection, Edge, IsValidConnection, Node, NodeTypes } from '@xyflow/react';

import { workflowAgentAtom } from '@frontend/atoms';
import { useInstanceId } from '@frontend/hooks';
import { Step, StepType, Trigger, TriggerType } from '@frontend/types';
import { ForLoopStep } from '@frontend/types/workflow-agent';

import { generateNodeId } from '../services/workflow-id-utils';
import {
  evaluateAndReplaceNodeType,
  isSpecializedToConditionConnection,
} from '../utils/nodeDataMapping';

import { useConnectionValidation } from './useConnectionValidation';
import { useNodeValidation } from './useNodeValidation';
import { useWorkflow } from './useWorkflow';
import { useWorkflowDnd } from './useWorkflowDnd';

export type UseWorkflowCanvasProps = {
  nodes: Node[];
  edges: Edge[];
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  onNodeDropped?: () => void;
  onAsyncNodeDropped?: (node: Node) => void;
  onFirstNodeDrop?: (updatedNodes: Node[]) => void;
};

export type UseWorkflowCanvasReturn = {
  // Event handlers
  onConnect: (params: Connection) => void;
  isValidConnection: IsValidConnection;
  onNodesDelete: (nodes: Node[]) => void;
  onEdgesDelete: (edges: Edge[]) => void;
  onNodeUpdate: (nodeId: string, data: Record<string, unknown>) => void;

  handleDragOver: (event: React.DragEvent) => void;
  handleDrop: (event: React.DragEvent) => void;

  nodeTypes: NodeTypes;
  reactFlowWrapper: RefObject<HTMLDivElement>;

  createAutoConnection: (triggerNode: Node, startStepNode: Node) => void;
  onDropNode: (
    nodeData: Step | Trigger,
    position: { x: number; y: number },
    forLoopContainer?: Node
  ) => void;
  onUpdateForLoopSteps: (forLoopNodeId: string, stepId: string, stepData: ForLoopStep) => void;
};

export function useWorkflowCanvas({
  nodes,
  edges,
  setNodes,
  setEdges,
  onNodeDropped,
  onAsyncNodeDropped,
  onFirstNodeDrop,
}: UseWorkflowCanvasProps): UseWorkflowCanvasReturn {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { validateConnection } = useConnectionValidation(nodes, edges);
  const { nodeTypes, getNodeConfig } = useWorkflow();
  const { clearNodeValidation, clearNodesValidation } = useNodeValidation();
  const [workflowAgent] = useAtom(workflowAgentAtom);
  const { instanceId } = useInstanceId();

  const isTriggerNode = useCallback((nodeData: Step | Trigger): nodeData is Trigger => {
    return Object.values(TriggerType).includes(nodeData.type as TriggerType);
  }, []);

  const findExistingTriggers = useCallback(
    (nodes: Node[]): Node[] => {
      return nodes.filter(node => {
        const nodeData = node.data as unknown as Step | Trigger;
        return isTriggerNode(nodeData);
      });
    },
    [isTriggerNode]
  );

  const findTriggerTarget = useCallback(
    (triggerNodes: Node[], edges: Edge[]): Node | null => {
      // Find what the existing triggers are connected to
      for (const trigger of triggerNodes) {
        const outgoingEdge = edges.find(edge => edge.source === trigger.id);
        if (outgoingEdge) {
          // Find the target node
          const targetNode = nodes.find(node => node.id === outgoingEdge.target);
          if (targetNode) {
            return targetNode;
          }
        }
      }
      return null;
    },
    [nodes]
  );

  // Auto-connection functionality for triggers
  const createAutoConnection = useCallback(
    (triggerNode: Node, startStepNode: Node) => {
      const newEdge: Edge = {
        id: `${triggerNode.id}-${startStepNode.id}`,
        source: triggerNode.id,
        target: startStepNode.id,
        sourceHandle: 'sources/main/0',
        targetHandle: 'targets/main/0',
        type: 'custom',
        deletable: true,
      };

      setEdges((eds: Edge[]) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  const onUpdateForLoopSteps = useCallback(
    (forLoopNodeId: string, stepId: string, stepData: ForLoopStep) => {
      setNodes(nds =>
        nds.map(node => {
          if (node.id === forLoopNodeId && node.type === 'for_loop') {
            const forLoopData = node.data as Record<string, unknown> & {
              steps?: Record<string, unknown>;
              start_step_id?: string;
            };

            // Ensure steps object exists, especially for nested ForLoops
            const currentSteps = (forLoopData.steps as Record<string, unknown>) || {};
            const updatedSteps = {
              ...currentSteps,
              [stepId]: stepData, // Use actual step ID as the key
            };

            // Set as start step if it's the first step
            const startStepId = forLoopData.start_step_id || stepId;

            return {
              ...node,
              data: {
                ...forLoopData,
                steps: updatedSteps,
                start_step_id: startStepId,
              },
            };
          }

          // Also update parent ForLoop nodes if this is a nested ForLoop being updated
          if (node.type === 'for_loop') {
            const forLoopContainer = nodes.find(n => n.id === forLoopNodeId);
            if (forLoopContainer?.parentId === node.id) {
              const parentForLoopData = node.data as Record<string, unknown> & {
                steps?: Record<string, unknown>;
              };

              // Update the parent's steps to include the updated nested ForLoop
              const parentSteps = parentForLoopData.steps as Record<string, ForLoopStep>;
              if (parentSteps && parentSteps[forLoopNodeId]) {
                const updatedParentSteps = {
                  ...parentSteps,
                  [forLoopNodeId]: {
                    ...parentSteps[forLoopNodeId],
                    // The nested ForLoop will be updated by the above condition
                  },
                };

                return {
                  ...node,
                  data: {
                    ...parentForLoopData,
                    steps: updatedParentSteps,
                  },
                };
              }
            }
          }

          return node;
        })
      );
    },
    [setNodes, nodes]
  );

  // Node operation methods
  const onDropNode = useCallback(
    (nodeData: Step | Trigger, position: { x: number; y: number }, forLoopContainer?: Node) => {
      let finalPosition = position;
      let parentId: string | undefined;
      let extent: 'parent' | undefined;

      if (forLoopContainer) {
        // Dropping inside ForLoop container
        parentId = forLoopContainer.id;
        extent = 'parent';

        // Adjust position to be relative to the ForLoop container
        const containerWidth = forLoopContainer.width || forLoopContainer.measured?.width || 320;
        const containerHeight = forLoopContainer.measured?.height || forLoopContainer.height || 188;

        finalPosition = {
          x: position.x - forLoopContainer.position.x,
          y: position.y - forLoopContainer.position.y,
        };

        // Ensure the node is positioned within the drop zone with better bounds
        const iconOffset = 48; // Space for the top-right icon
        const padding = 20;
        finalPosition.x = Math.max(
          padding,
          Math.min(finalPosition.x, containerWidth - padding - 240)
        ); // Account for node width
        finalPosition.y = Math.max(
          padding + iconOffset,
          Math.min(finalPosition.y, containerHeight - padding - 60)
        ); // Account for node height
      }

      // Generate node ID - use pure UUID for all nodes
      const newNodeId = generateNodeId();

      const baseNode: Node = {
        id: newNodeId,
        type: nodeData.type,
        position: finalPosition,
        data: {
          ...(nodeData as unknown as Record<string, unknown>),
          step_id: newNodeId,
        },
        deletable: true,
        parentId,
        extent,
      };

      // Add explicit dimensions for ForLoop nodes to support parent-child relationships
      const newNode: Node =
        nodeData.type === 'for_loop'
          ? {
              ...baseNode,
              style: {
                width: 320,
                height: 188,
              },
              width: 320,
              height: 188,
            }
          : baseNode;

      setNodes(nds => {
        const updatedNodes = nds.concat(newNode);

        // Auto-connect trigger based on new logic
        if (isTriggerNode(nodeData) && !forLoopContainer) {
          // Check existing triggers BEFORE adding the new one (using current nodes, not updatedNodes)
          const existingTriggers = findExistingTriggers(nds);

          if (existingTriggers.length === 0) {
            // First trigger - no auto-connection
            // Skip connection for the first trigger
          } else {
            // Subsequent trigger - connect to same target as existing triggers
            const existingTarget = findTriggerTarget(existingTriggers, edges);
            if (existingTarget) {
              createAutoConnection(newNode, existingTarget);
            }
          }
        }

        // Close any existing right sidebar when a new node is dropped
        if (onNodeDropped) {
          onNodeDropped();
        }

        // Check if this is an async node and trigger callback
        const nodeType = getNodeConfig(nodeData.type);
        if (nodeType?.onAsyncSave && onAsyncNodeDropped) {
          onAsyncNodeDropped(newNode);
        }

        // Check if this is the first node dropped and trigger auto-save
        if (onFirstNodeDrop && nds.length === 0) {
          // Pass the updated nodes to the auto-save handler
          onFirstNodeDrop(updatedNodes);
        }

        return updatedNodes;
      });

      // If dropping inside ForLoop, also update the ForLoop's steps data
      if (forLoopContainer) {
        const forLoopStep: ForLoopStep = {
          step_id: newNodeId, // Use the UUID as step ID
          name:
            ((nodeData as unknown as Record<string, unknown>).name as string) ||
            `Step ${newNodeId}`,
          description: (nodeData as unknown as Record<string, unknown>).description as string,
          type: nodeData.type as StepType,
          position: finalPosition,
          next_step_id: null,
          ...(nodeData as unknown as Record<string, unknown>), // Spread remaining properties from nodeData
        };

        onUpdateForLoopSteps(forLoopContainer.id, newNodeId, forLoopStep);
      }

      // Local state is already updated via setNodes above
    },
    [
      isTriggerNode,
      findExistingTriggers,
      findTriggerTarget,
      edges,
      setNodes,
      createAutoConnection,
      onUpdateForLoopSteps,
      onAsyncNodeDropped,
      onFirstNodeDrop,
      onNodeDropped,
      getNodeConfig,
    ]
  );

  // Drag and drop functionality
  const { onDragOver, onDrop } = useWorkflowDnd({
    reactFlowWrapper,
    nodes,
    onDropNode,
  });
  const isValidConnection: IsValidConnection = useCallback(
    (connection: Edge | Connection) => {
      const sourceNode = nodes.find(n => n.id === connection.source);
      const targetNode = nodes.find(n => n.id === connection.target);

      if (!sourceNode || !targetNode) return false;

      const validationResult = validateConnection(
        sourceNode,
        targetNode,
        connection.sourceHandle || undefined,
        connection.targetHandle || undefined
      );

      return validationResult.isValid;
    },
    [nodes, validateConnection]
  );

  const onConnect = useCallback(
    (params: Connection) => {
      // First add the edge using addEdge
      setEdges((eds: Edge[]) =>
        addEdge(
          {
            ...params,
            deletable: true,
            type: 'custom',
          },
          eds
        )
      );

      // Find the source and target nodes for potential transformation
      const sourceNode = nodes.find(n => n.id === params.source);
      const targetNode = nodes.find(n => n.id === params.target);

      // Check if this connection should trigger a node replacement
      if (sourceNode && targetNode && isSpecializedToConditionConnection(sourceNode, targetNode)) {
        // Create the new edge for evaluation (since state update is async)
        const newEdge = {
          id: `${params.source}-${params.target}`,
          ...params,
          deletable: true,
          type: 'custom',
        } as Edge;

        const updatedEdges = [...edges, newEdge];

        // Evaluate if the source node needs replacement based on the new connection
        const replacementResult = evaluateAndReplaceNodeType(sourceNode.id, nodes, updatedEdges);

        if (
          replacementResult.shouldReplace &&
          replacementResult.newNode &&
          replacementResult.updatedEdges
        ) {
          // Replace the old node with the new one
          const updatedNodes = nodes.map(node =>
            node.id === sourceNode.id ? replacementResult.newNode : node
          );

          setNodes(updatedNodes);
          setEdges(replacementResult.updatedEdges);

          // Local state is already updated via setNodes above
        }
      }
    },
    [setEdges, nodes, setNodes, edges]
  );

  const onNodeUpdate = useCallback(
    (nodeId: string, data: Record<string, unknown>) => {
      const updatedNodes = nodes.map(node =>
        node.id === nodeId ? { ...node, data: { ...node.data, ...data } } : node
      );

      clearNodeValidation(nodeId);

      setNodes(updatedNodes);

      // Local state is already updated via setNodes above
    },
    [nodes, setNodes, clearNodeValidation]
  );

  // Helper function for sophisticated node deletion
  const deleteSingleNode = useCallback(
    async (node: Node): Promise<boolean> => {
      const nodeType = getNodeConfig(node.type || '');

      // Check if node supports async deletion and has backend data
      if (nodeType?.onAsyncDelete && instanceId && workflowAgent.id) {
        const nodeData = node.data as Record<string, unknown>;
        const hasBackendData = nodeData && 'links' in nodeData && nodeData.links;

        if (hasBackendData) {
          try {
            // Call the node type's async delete function directly
            const context = {
              instanceId,
              workflowAgentId: workflowAgent.id,
              nodeData,
            };

            await nodeType.onAsyncDelete(node.id, context);
            // Don't show success toast here - node type handles its own toast
            return true; // Indicates async deletion was used
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to delete node';
            toaster.create(errorMessage, { intent: 'danger' });
            throw error; // Re-throw to stop deletion process
          }
        }
      }

      // For nodes without backend data or async delete capability
      return false; // Indicates regular deletion should be used
    },
    [instanceId, workflowAgent.id, getNodeConfig]
  );

  const onNodesDelete = useCallback(
    async (deletedNodes: Node[]) => {
      const deletedNodeIds = deletedNodes.map(node => node.id);

      clearNodesValidation(deletedNodeIds);

      // Track which nodes were handled by async deletion
      const asyncDeletedNodes: string[] = [];
      let hasErrors = false;

      // Process each node for sophisticated deletion
      for (const node of deletedNodes) {
        try {
          const usedAsyncDelete = await deleteSingleNode(node);
          if (usedAsyncDelete) {
            asyncDeletedNodes.push(node.id);
          }
        } catch (_error) {
          hasErrors = true;
          break; // Stop deletion process on error
        }
      }

      // If there were errors, don't proceed with any deletions
      if (hasErrors) {
        return;
      }

      // Show success toast for nodes that used regular deletion
      const regularDeletedNodes = deletedNodes.filter(node => !asyncDeletedNodes.includes(node.id));
      if (regularDeletedNodes.length > 0) {
        const message =
          regularDeletedNodes.length === 1
            ? 'Node deleted successfully'
            : `${regularDeletedNodes.length} nodes deleted successfully`;
        toaster.create(message, { intent: 'success' });
      }

      // Update local state - remove all deleted nodes
      const updatedNodes = nodes.filter(node => !deletedNodeIds.includes(node.id));

      // Remove edges connected to deleted nodes
      const updatedEdges = edges.filter(
        edge => !deletedNodeIds.includes(edge.source) && !deletedNodeIds.includes(edge.target)
      );

      // Check for reverse transformations after node deletion
      // Find nodes that were connected to deleted condition nodes
      const affectedNodeIds = new Set<string>();
      deletedNodes.forEach(deletedNode => {
        if (deletedNode.type === 'condition') {
          // Find nodes that were connected to this condition node
          edges.forEach(edge => {
            if (edge.target === deletedNode.id) {
              affectedNodeIds.add(edge.source);
            }
          });
        }
      });

      let currentNodes = updatedNodes;
      let currentEdges = updatedEdges;

      // Check each affected node for potential transformation
      affectedNodeIds.forEach(nodeId => {
        const replacementResult = evaluateAndReplaceNodeType(nodeId, currentNodes, currentEdges);

        if (
          replacementResult.shouldReplace &&
          replacementResult.newNode &&
          replacementResult.updatedEdges
        ) {
          // Apply the transformation
          currentNodes = currentNodes.map(node =>
            node.id === nodeId ? replacementResult.newNode : node
          );
          currentEdges = replacementResult.updatedEdges;
        }
      });

      // Update state with final results
      setNodes(currentNodes);
      setEdges(currentEdges);
    },
    [nodes, edges, setNodes, setEdges, clearNodesValidation, deleteSingleNode]
  );

  const onEdgesDelete = useCallback(
    (deletedEdges: Edge[]) => {
      const deletedEdgeIds = deletedEdges.map(edge => edge.id);
      const updatedEdges = edges.filter(edge => !deletedEdgeIds.includes(edge.id));
      setEdges(updatedEdges);

      // Check for reverse transformations after edge deletion
      const affectedNodeIds = new Set([
        ...deletedEdges.map(edge => edge.source),
        ...deletedEdges.map(edge => edge.target),
      ]);

      let currentNodes = nodes;
      let currentEdges = updatedEdges;

      // Check each affected node for potential transformation
      affectedNodeIds.forEach(nodeId => {
        const replacementResult = evaluateAndReplaceNodeType(nodeId, currentNodes, currentEdges);

        if (
          replacementResult.shouldReplace &&
          replacementResult.newNode &&
          replacementResult.updatedEdges
        ) {
          // Apply the transformation
          currentNodes = currentNodes.map(node =>
            node.id === nodeId ? replacementResult.newNode : node
          );
          currentEdges = replacementResult.updatedEdges;
        }
      });

      // Update state if any transformations occurred
      if (currentNodes !== nodes) {
        setNodes(currentNodes);
      }
      if (currentEdges !== updatedEdges) {
        setEdges(currentEdges);
      }
    },
    [edges, setEdges, nodes, setNodes]
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      if (onDragOver) {
        onDragOver(event);
      }
    },
    [onDragOver]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      if (onDrop) {
        onDrop(event);
      }
    },
    [onDrop]
  );

  return {
    onConnect,
    isValidConnection,
    onNodesDelete,
    onEdgesDelete,
    onNodeUpdate,
    handleDragOver,
    handleDrop,
    nodeTypes,
    reactFlowWrapper,
    createAutoConnection,
    onDropNode,
    onUpdateForLoopSteps,
  };
}
